<template>
  <div>
    <p class="d-flex align-center mb-4 text--small text--bold text-text-primary">
      <span class="stepper-number">1</span>{{ $t('project.checklist.dialogs.ImportChecklistDialog.downloadOurExcelTemplate') }}
    </p>
    <AppDownloadFile class="mb-6" :link="templateLink">
      <template #default="{ submitForm }">
        <app-button type="outlined"
                    variant="neutral"
                    @click="submitForm"
        >
          {{ $t('project.checklist.dialogs.ImportChecklistDialog.downloadExcelTemplate') }}
        </app-button>
      </template>
    </AppDownloadFile>

    <p class="d-flex align-center mb-4 text--small text--bold text-text-primary">
      <span class="stepper-number">2</span>{{ $t('project.checklist.dialogs.ImportChecklistDialog.uploadExcelFile') }}
    </p>
    <template v-if="isQuestionAnswerDialog">
      <IdentityCheckingIDFileInput refValue="QAExcelFileInput"
                                   describedby="QAExcelFileUploadButton"
                                   :allowedExtensions="allowedExtensions"
                                   :isDocumentErrored="!!hasImportErrors"
                                   @update-file="file = $event; updateFile()"
      >
      </IdentityCheckingIDFileInput>
    </template>
    <template v-else>
      <!-- File name -->
      <div v-if="file" class="imported-file text-text-secondary w-100" :class="{ 'imported-file--error': hasImportErrors }">
        {{ file.name }}
        <app-button type="icon" class="ml-auto" @click="removeFile">
          <app-material-icon name="close" />
        </app-button>
      </div>
      <!-- Dropzone -->
      <div v-else class="excel-file-dropzone">
        <AppDroppableArea class="excel-file-dropzone__droppable-area pa-12"
                          @ondrop="dropFile">
          <OfficeIcon
            document-type="excel"
            icon-type="product"
            :width="50"
            :height="50"
          />
          <input ref="fileInput"
                 class="d-none"
                 type="file"
                 :accept="allowedExtensions"
                 @input="addFile"
          >
          <p class="mt-6 text--small text--bold text-text-primary">
            {{ $t('project.checklist.dialogs.ImportChecklistDialog.dropZoneText1') }}
          </p>
          <p class="text--small text-text-secondary">
            {{ $t('project.checklist.dialogs.ImportChecklistDialog.dropZoneText2') }}
          </p>
          <app-button class="mt-4" @click="openFileInput">
            {{ $t('project.checklist.dialogs.ImportChecklistDialog.importFromComputer') }}
          </app-button>
        </AppDroppableArea>
      </div>
    </template>
  </div>
</template>

<script>
  import { getFilesFromDataTransferItems } from 'datatransfer-files-promise'

  import AppDownloadFile from '@/common/AppDownloadFile.vue'
  import AppDroppableArea from '@/common/AppDroppableArea.vue'
  import IdentityCheckingIDFileInput from '@/common/identity-checking/IdentityCheckingIDFileInput.vue'
  import OfficeIcon from '@/common/office/OfficeIcon.vue'

  export default {
    name: 'ImportChecklistDialogExcelFile',
    components: {
      AppDownloadFile,
      AppDroppableArea,
      IdentityCheckingIDFileInput,
      OfficeIcon,
    },
    props: {
      isQuestionAnswerDialog: {
        type: Boolean,
        default: false,
      },
      templateLink: {
        type: String,
        required: true,
      },
      existingFile: {
        validator: (value) => {
          return value === null || typeof value === 'object'
        },
      },
      allowedExtensions: {
        type: Array,
        default: () => ['.xls', '.xlsx'],
      },
      hasImportErrors: {
        type: Boolean,
        default: true,
      },
    },
    data () {
      return {
        file: null,
      }
    },
    created () {
      if (this.existingFile) {
        this.file = this.existingFile
      }
    },
    methods: {
      openFileInput () {
        this.$refs.fileInput.click()
      },
      addFile (event) {
        this.file = event.target.files[0]
        this.updateFile()
      },
      async dropFile (event) {
        const dataTransferItems = event.dataTransfer?.items
        if (dataTransferItems) {
          const droppedFiles = await getFilesFromDataTransferItems(dataTransferItems)
          this.file = droppedFiles[0]
          this.updateFile()
        }
      },
      removeFile () {
        this.file = null
        this.updateFile()
        if (this.hasImportErrors) {
          this.$emit('reset-import-errors')
        }
      },
      updateFile () {
        this.$emit('update-file', this.file)
      },
    },
  }
</script>

<style scoped lang="scss">
  .stepper-number {
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
    color: rgb(var(--v-theme-text-grey));
    background-color: rgb(var(--v-theme-surface-grey));
    border-radius: 50%;
    font-size: 12px;
    font-weight: 500;
  }

  .excel-file-dropzone {
    border: 2px dashed rgb(var(--v-theme-border-primary));
    border-radius: 7px;

    &:has(.drag-in-progress) {
      border: none;
    }

    &__droppable-area {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
  .imported-file {
    display: inline-flex;
    align-items: center;
    padding: 4px 16px;
    border-radius: 4px;
    border: 1px solid rgb(var(--v-theme-border-secondary));
    background: rgb(var(--v-theme-surface-secondary));
    &--error {
      border: 1px solid rgb(var(--v-theme-border-danger));
    }
  }
</style>
